import React, { useMemo, useState } from 'react'
import { ChevronLeft, Menu } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import { useTranslation } from '@/features/locale/LocaleProvider'
import { useMobileShellMenuOptional } from '@/mobile/platform/MobileShellMenuContext'
import CurrencyLogo from '@/components/CurrencyLogo'
import {
  isContractDiscoveryActive,
  TokenPickerAddressSearchBlock,
  TokenPickerListRow,
  TokenPickerNoResultsView,
  TokenPickerSearchField,
} from '@/components/token-picker'
import { useAppWeb3 } from '@/hooks'
import { Currency, ChainId, CurrencyAmount, currencyEquals } from '@/sdk'

import {
  currencyKey,
  useMobileTokenPickerGroups,
  type MobileTokenPickerMode,
} from './useMobileTokenPickerGroups'

const labels = nutrimaticLabels.mobileSimple

function withoutCurrency(currencies: Currency[], exclude?: Currency | null): Currency[] {
  if (!exclude) return currencies
  return currencies.filter((currency) => !currencyEquals(currency, exclude))
}

function MobileNutrimaticPickerHeader({
  title,
  onBack,
  backLabel,
}: {
  title: string
  onBack: () => void
  backLabel?: string
}) {
  const shellMenu = useMobileShellMenuOptional()
  const t = useTranslation()

  return (
    <header className="nm-mobile-simple__hero-header nm-mobile-simple__token-sheet-header">
      <div className="nm-mobile-simple__hero-toolbar">
        <div className="nm-mobile-simple__hero-toolbar-start">
          <button
            type="button"
            className="nm-mobile-simple__icon-btn"
            onClick={onBack}
            aria-label={backLabel ?? nutrimaticLabels.cancel}
          >
            <ChevronLeft className="size-5" aria-hidden />
          </button>
        </div>
        <div className="nm-mobile-simple__hero-heading">
          <h1 className="nm-mobile-simple__hero-title">{title}</h1>
        </div>
        <div className="nm-mobile-simple__hero-trailing">
          {shellMenu ? (
            <button
              type="button"
              className="nm-mobile-simple__icon-btn"
              onClick={shellMenu.openMenu}
              aria-label={t.common.openMenu}
            >
              <Menu className="size-5" aria-hidden />
            </button>
          ) : (
            <span className="nm-mobile-simple__icon-btn" aria-hidden />
          )}
        </div>
      </div>
    </header>
  )
}

function PickerSectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="nm-mobile-simple__picker-section-title">{children}</h2>
}

function MobileTokenReceiveFeaturedCard({
  currency,
  onSelect,
}: {
  currency: Currency
  onSelect: (currency: Currency) => void
}) {
  const symbol = currency.symbol ?? '—'
  const name = currency.name ?? symbol

  return (
    <button
      type="button"
      className="nm-mobile-simple__picker-featured-card"
      onClick={() => onSelect(currency)}
    >
      <span className="nm-mobile-simple__picker-featured-logo">
        <CurrencyLogo currency={currency} size="32px" />
      </span>
      <span className="nm-mobile-simple__picker-featured-symbol">{symbol}</span>
      <span className="nm-mobile-simple__picker-featured-name">{name}</span>
    </button>
  )
}

function MobileTokenSearchResults({
  tokens,
  balanceByKey,
  onSelect,
  showEmpty,
  onClearSearch,
}: {
  tokens: Currency[]
  balanceByKey: Map<string, CurrencyAmount>
  onSelect: (currency: Currency) => void
  showEmpty?: boolean
  onClearSearch: () => void
}) {
  if (tokens.length === 0) {
    return showEmpty ? (
      <TokenPickerNoResultsView variant="nutrimatic" onCancel={onClearSearch} />
    ) : null
  }

  return (
    <div className="nm-mobile-simple__picker-section">
      <PickerSectionTitle>{labels.pickerResults}</PickerSectionTitle>
      <div className="nm-mobile-simple__picker-list">
        {tokens.map((currency) => (
          <TokenPickerListRow
            key={currencyKey(currency)}
            currency={currency}
            balance={balanceByKey.get(currencyKey(currency))}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

/** Picker Nutrimatic reutilizable (swap, liquidez, etc.). */
export function MobileNutrimaticTokenPicker({
  chainId,
  mode,
  title,
  onSelect,
  onBack,
  account: accountProp,
  excludeCurrency,
}: {
  chainId: ChainId
  mode: MobileTokenPickerMode
  title: string
  onSelect: (currency: Currency) => void
  onBack: () => void
  account?: string | null
  excludeCurrency?: Currency | null
}) {
  const { account: connectedAccount } = useAppWeb3()
  const account = accountProp ?? connectedAccount
  const [search, setSearch] = useState('')
  const {
    tokens,
    searchToken,
    isAddressSearch,
    isResolvingAddress,
    importedByAddress,
    addressNotFound,
    isSearching,
    payGroups,
    receiveGroups,
    balanceByKey,
  } = useMobileTokenPickerGroups(chainId, search, account, mode)

  const filteredTokens = useMemo(() => withoutCurrency(tokens, excludeCurrency), [excludeCurrency, tokens])
  const filteredPayGroups = useMemo(
    () => ({
      withBalance: payGroups.withBalance.filter(
        ({ currency }) => !excludeCurrency || !currencyEquals(currency, excludeCurrency),
      ),
      withoutBalance: withoutCurrency(payGroups.withoutBalance, excludeCurrency),
    }),
    [excludeCurrency, payGroups],
  )
  const filteredReceiveGroups = useMemo(
    () => ({
      featured: withoutCurrency(receiveGroups.featured, excludeCurrency),
      all: withoutCurrency(receiveGroups.all, excludeCurrency),
    }),
    [excludeCurrency, receiveGroups],
  )

  const showSearchEmpty =
    isSearching && !isResolvingAddress && !importedByAddress && !addressNotFound && filteredTokens.length === 0
  const contractDiscovery = isContractDiscoveryActive(importedByAddress, searchToken)

  return (
    <div className="nm-mobile-simple__token-sheet-layout">
      <MobileNutrimaticPickerHeader title={title} onBack={onBack} />

      <div
        className="nm-mobile-simple__body nm-mobile-simple__body--scroll nm-mobile-simple__token-sheet-body nm-mobile-simple__picker-body flex min-h-0 flex-1 flex-col"
        data-mobile-scroll-container
      >
        <TokenPickerSearchField value={search} onChange={setSearch} />

        <TokenPickerAddressSearchBlock
          isAddressSearch={isAddressSearch}
          isResolvingAddress={isResolvingAddress}
          importedByAddress={importedByAddress}
          addressNotFound={addressNotFound}
          searchToken={searchToken}
          onImport={() => setSearch('')}
          onCancel={() => setSearch('')}
          variant="nutrimatic"
        />

        {contractDiscovery ? null : isSearching ? (
          <MobileTokenSearchResults
            tokens={filteredTokens}
            balanceByKey={balanceByKey}
            onSelect={onSelect}
            showEmpty={showSearchEmpty}
            onClearSearch={() => setSearch('')}
          />
        ) : mode === 'pay' ? (
          <>
            {!account ? (
              <p className="nm-mobile-simple__picker-hint">{labels.pickerConnectWalletHint}</p>
            ) : null}

            <div className="nm-mobile-simple__picker-section">
              <PickerSectionTitle>{labels.pickerWithBalance}</PickerSectionTitle>
              {filteredPayGroups.withBalance.length > 0 ? (
                <div className="nm-mobile-simple__picker-list">
                  {filteredPayGroups.withBalance.map(({ currency, balance }) => (
                    <TokenPickerListRow
                      key={currencyKey(currency)}
                      currency={currency}
                      balance={balance}
                      onSelect={onSelect}
                    />
                  ))}
                </div>
              ) : (
                <p className="nm-mobile-simple__picker-empty">{labels.pickerEmptyWithBalance}</p>
              )}
            </div>

            <div className="nm-mobile-simple__picker-section">
              <PickerSectionTitle>{labels.pickerWithoutBalance}</PickerSectionTitle>
              {filteredPayGroups.withoutBalance.length > 0 ? (
                <div className="nm-mobile-simple__picker-list">
                  {filteredPayGroups.withoutBalance.map((currency) => (
                    <TokenPickerListRow key={currencyKey(currency)} currency={currency} onSelect={onSelect} />
                  ))}
                </div>
              ) : (
                <p className="nm-mobile-simple__picker-empty">{labels.pickerNoResults}</p>
              )}
            </div>
          </>
        ) : mode === 'list' ? (
          <div className="nm-mobile-simple__picker-section">
            <PickerSectionTitle>{labels.pickerAll}</PickerSectionTitle>
            {filteredReceiveGroups.all.length > 0 ? (
              <div className="nm-mobile-simple__picker-list">
                {filteredReceiveGroups.all.map((currency) => (
                  <TokenPickerListRow
                    key={currencyKey(currency)}
                    currency={currency}
                    balance={balanceByKey.get(currencyKey(currency))}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            ) : (
              <p className="nm-mobile-simple__picker-empty">{labels.pickerNoResults}</p>
            )}
          </div>
        ) : (
          <>
            {filteredReceiveGroups.featured.length > 0 ? (
              <div className="nm-mobile-simple__picker-section">
                <PickerSectionTitle>{labels.pickerFeatured}</PickerSectionTitle>
                <div className="nm-mobile-simple__picker-featured-grid">
                  {filteredReceiveGroups.featured.map((currency) => (
                    <MobileTokenReceiveFeaturedCard key={currencyKey(currency)} currency={currency} onSelect={onSelect} />
                  ))}
                </div>
              </div>
            ) : null}

            <div className="nm-mobile-simple__picker-section">
              <PickerSectionTitle>{labels.pickerAll}</PickerSectionTitle>
              {filteredReceiveGroups.all.length > 0 ? (
                <div className="nm-mobile-simple__picker-list">
                  {filteredReceiveGroups.all.map((currency) => (
                    <TokenPickerListRow key={currencyKey(currency)} currency={currency} onSelect={onSelect} />
                  ))}
                </div>
              ) : (
                <p className="nm-mobile-simple__picker-empty">{labels.pickerNoResults}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
